import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _descriptor2, _descriptor3;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbField } from "abreact/form/AbField";
import { AbFieldGroup } from "abreact/form/AbFieldGroup";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbTabControl, AbTabControlTheme } from "abreact/incubator/ui/component/AbTabControl";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { AbDialog } from "abreact/popup/AbDialog";
import { URLUtils } from "abweb/utils/URLUtils";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
/**
 * Dialog for creating or editing a markdown link.
 * If no AbMarkdownLinkExtensions are registered, shows editor for web links.
 * Otherwise, a tabbed dialog is shown, one tab per extension, with the last tab is the field group.
 * The contents of the extension tabs are the editComponents from the extension.
 */
export let AbMarkdownLinkDialog = observer(_class = (_class2 = class AbMarkdownLinkDialog extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "mode", void 0);
    // is this dialog creating a new image, or editing an existing one?
    _initializerDefineProperty(this, "text", _descriptor, this);
    _initializerDefineProperty(this, "url", _descriptor2, this);
    _initializerDefineProperty(this, "currentTab", _descriptor3, this);
    _defineProperty(this, "onOk", async () => {
      let url = this.url;

      // special case: url === "" means delete
      if (url.length > 0) {
        try {
          const parsedUrl = new URL(url);
        } catch (e) {
          // can't parse.  Add "http:"  If it doesn't start with "//" add that too.
          url = "http:" + (url.startsWith("//") ? "" : "//") + url;
        }
      }
      return this.props.onOk(this.text, url);
    });
    _defineProperty(this, "onClose", () => {
      this.props.onClose();
    });
    makeObservable(this);
    this.mode = this.props.buttonEl ? "create" : "edit";
    this.text = this.props.text;
    this.url = this.props.url;

    // Use the URL to select the correct tab
    // ABSDK-3489: only if the tab will be rendered
    const protocol = URLUtils.getProtocol(this.url);
    if (protocol && AbMarkdownExtensions.markdownLinkExtensions.get(protocol)?.renderEditor) {
      this.setCurrentTab(protocol);
    }
  }
  setCurrentTab(x) {
    this.currentTab = x;
  }
  render() {
    let tabs = [];

    // Add a tab for all registered extensions
    AbMarkdownExtensions.markdownLinkExtensions.get().forEach((extension, protocol) => {
      if (extension.renderEditor) {
        tabs.push({
          id: protocol,
          label: extension.tabText,
          content: extension.renderEditor(this.url, this.text, action((href, text) => {
            this.text = text;
            this.url = href;
          }))
        });
      }
    });

    // Default tab is the web tab
    const webTab = /*#__PURE__*/React.createElement(AbFieldGroup, {
      hideDecorators: true
    }, /*#__PURE__*/React.createElement(AbField, {
      label: AbReactResources.markdownEditor_linkText(),
      fullWidth: true
    }, /*#__PURE__*/React.createElement(AbTextInput, {
      dataTestId: "markdown-link-text",
      value: this.text,
      onChange: newVal => runInAction(() => this.text = newVal)
    })), /*#__PURE__*/React.createElement(AbField, {
      label: AbReactResources.markdownEditor_url(),
      fullWidth: true
    }, /*#__PURE__*/React.createElement(AbTextInput, {
      dataTestId: "markdown-link-url",
      value: this.url,
      onChange: newVal => runInAction(() => this.url = newVal)
    })));

    // if we have any extensions, add the web tab to them, otherwise we'll render just the web tab
    if (tabs.length > 0) {
      tabs.push({
        id: "web",
        label: "Web link",
        content: webTab
      });
    }
    const dialogContents = tabs.length > 0 ? /*#__PURE__*/React.createElement(AbTabControl, {
      theme: AbTabControlTheme.PRIMARY,
      tabs: tabs,
      activeTab: this.currentTab,
      onChange: newTab => this.setCurrentTab(newTab.id)
    }) : webTab;
    if (this.mode === "create") {
      return /*#__PURE__*/React.createElement(AbDialog, {
        origin: this.props.buttonEl,
        position: PopupPosition.BELOW,
        resizable: false,
        closeButton: false,
        cancelButton: true,
        submitButton: AbReactResources.markdownEditor_insert(),
        onSubmit: this.onOk,
        onClose: this.onClose,
        className: CssUtils.composeClassNames("ab-markdown-link-dialog", tabs.length > 0 ? "with-tabs" : null)
      }, dialogContents);
    } else {
      return /*#__PURE__*/React.createElement(AbDialog, {
        title: AbReactResources.markdownEditor_editLinkDialogTitle(),
        closeButton: false,
        submitButton: "OK",
        onSubmit: this.onOk,
        cancelButton: true,
        onClose: this.onClose,
        className: CssUtils.composeClassNames("ab-markdown-link-dialog", tabs.length > 0 ? "with-tabs" : null)
      }, dialogContents);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class2.prototype, "text", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "url", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "currentTab", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return "web";
  }
}), _applyDecoratedDescriptor(_class2.prototype, "setCurrentTab", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "setCurrentTab"), _class2.prototype), _class2)) || _class;
//# sourceMappingURL=AbMarkdownLinkDialog.js.map
